import {
  CommunicationCategory,
  CommunicationDirection,
  TaskStatus,
  TEXT_CONTENT_PAYLOAD_ID,
} from '@bariendo/lib/constants';
import { getCommunicationDirection, getFromNumber, isFaxCommunication } from '@bariendo/lib/communication';
import { deriveVoiceCallDisplayStatus } from '@bariendo/lib/voice-call-outcome';
import { formatHumanName, getExtensionValue } from '@medplum/core';
import { Communication, Task } from '@medplum/fhirtypes';
import { isCommunicationFocusResource } from '@bariendo/lib/inbox-tasks';
import { NotificationItem } from './types';
import { getPriorityStatus } from '@/services/priority-management';
import { CommunicationFragment, NotificationTask } from './graphql';
import { hasCode, isReferenceOf } from '@bariendo/lib/services';
import { FAX_ORIGINATING_NUMBER } from '@bariendo/lib/extensions';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';

/**
 * SMS from an unregistered phone: inbound SMS Communication with sender/subject omitted and no Task.for patient.
 */
export function isUnknownNumberSmsNotification(
  inboxTask: Pick<Task, 'for'>,
  communicationfragment?: CommunicationFragment
): boolean {
  if (!communicationfragment) {
    return false;
  }
  const isSms = hasCategory(communicationfragment, CommunicationCategory.SMS_MESSAGE);
  const isIncoming = Boolean(communicationfragment.category?.some(hasCode(CommunicationDirection.INCOMING)));
  return Boolean(isSms && isIncoming && !communicationfragment.sender && !inboxTask.for);
}

function hasCategory(communicationfragment: CommunicationFragment, category: CommunicationCategory) {
  return communicationfragment.category?.some(hasCode(category));
}

/**
 * Transforms Task with resources NotificationItems
 * Handles both Communication-focused tasks (special case) and generic resource-focused tasks
 * @param tasks - Array of Task and related resources
 */
export function getNotificationItemsFromTasks(tasks: NotificationTask[]): NotificationItem[] {
  const notifications: NotificationItem[] = [];
  for (const task of tasks) {
    const focusResource = task.focus?.resource;
    const sourceCommunication = isCommunicationFocusResource(focusResource)
      ? (focusResource as CommunicationFragment)
      : undefined;

    const content = getNotificationContent(task, sourceCommunication);
    const isRead = task.status === TaskStatus.IN_PROGRESS;
    const isArchived = task.status === TaskStatus.COMPLETED;
    const priorityStatus = getPriorityStatus(task.priority, task.executionPeriod);
    const name = getNotificationDisplayName(task, sourceCommunication);

    // Prefer Communication.sent, fallback to Task.authoredOn, then current date
    const timestamp = sourceCommunication?.sent
      ? new Date(sourceCommunication.sent)
      : task.authoredOn
        ? new Date(task.authoredOn)
        : new Date();

    notifications.push({
      task,
      id: task.id!,
      sourceCommunication,
      content,
      isRead,
      isArchived,
      priorityStatus,
      name,
      timestamp,
    });
  }
  return notifications;
}

/**
 * What a phone-call row says it is.
 *
 * Every call row used to fall through to a hardcoded "Missed Call", including
 * answered and recorded ones whose push notification said "New call recording"
 * at the same moment. Derived from the call's outcome instead — FHIR status
 * cannot answer it, because a missed call completes like any other.
 */
function getPhoneCallContent(communication: Communication): string {
  switch (deriveVoiceCallDisplayStatus(communication)) {
    case 'missed-call':
      return 'Missed Call';
    case 'forwarded':
      return 'Forwarded Call';
    case 'no-answer':
      return 'No Answer';
    case 'failed':
      return 'Failed Call';
    case 'preparing':
    case 'in-progress':
      return 'Call In Progress';
    default:
      return getCommunicationDirection(communication) === CommunicationDirection.OUTGOING
        ? 'Outgoing Call'
        : 'Incoming Call';
  }
}

/**
 * Gets notification content from Task and optional Communication
 * Handles both Communication-focused tasks (special case) and generic tasks
 * @param task - The notification task
 * @param communicationfragment - Optional Communication resource if focus is a Communication
 * @returns The content string for display
 */
function getNotificationContent(task: NotificationTask, communicationfragment?: CommunicationFragment): string {
  // Special case: Communication-focused tasks
  if (communicationfragment) {
    if (isFaxCommunication(communicationfragment)) {
      if (communicationfragment.sender?.display) {
        return `Fax received from ${communicationfragment.sender.display}`;
      }
      const originatingNumber = getExtensionValue(communicationfragment, FAX_ORIGINATING_NUMBER);
      if (originatingNumber && typeof originatingNumber === 'string') {
        return `Fax received from ${formatPhoneNumber(originatingNumber)}`;
      }
      return 'Fax received from unknown number';
    }

    if (!communicationfragment.payload?.length) {
      return 'Sensitive information';
    }

    const isMessageCommunication =
      hasCategory(communicationfragment, CommunicationCategory.CHAT) ||
      hasCategory(communicationfragment, CommunicationCategory.SMS_MESSAGE) ||
      hasCategory(communicationfragment, CommunicationCategory.STANDALONE_NOTE) ||
      hasCategory(communicationfragment, CommunicationCategory.TIMELINE_NOTE);

    if (isMessageCommunication) {
      return (
        communicationfragment.payload?.find((p) => p.id === TEXT_CONTENT_PAYLOAD_ID)?.contentString || 'No content'
      );
    }

    if (hasCategory(communicationfragment, CommunicationCategory.SALES_TEAM_FORWARD)) {
      return 'Call Forwarded to Close';
    }

    if (hasCategory(communicationfragment, CommunicationCategory.VIRTUAL_RECEPTIONIST_FORWARD)) {
      return 'Call Forwarded to Answer Connect';
    }

    if (hasCategory(communicationfragment, CommunicationCategory.PHONE_CALL)) {
      return getPhoneCallContent(communicationfragment as Communication);
    }

    return 'Missed Call';
  }

  // Generic case: Non-Communication tasks (use task description or generic message)
  return task.description || 'Notification';
}

function getNotificationDisplayName(
  inboxTask: NotificationTask,
  communicationfragment?: CommunicationFragment
): string {
  const resource = inboxTask.for?.resource;

  if (resource && 'organizationName' in resource && resource.organizationName) return resource.organizationName;

  if (resource && 'patientName' in resource && resource.patientName?.[0])
    return formatHumanName(resource.patientName[0]);

  if (communicationfragment && isFaxCommunication(communicationfragment)) {
    return 'No linked patient';
  }

  if (communicationfragment && isUnknownNumberSmsNotification(inboxTask, communicationfragment)) {
    const phone = getFromNumber(communicationfragment as Communication);
    return phone ? `Unknown number · ${formatPhoneNumber(phone)}` : 'Unknown number';
  }

  const resourceType = isReferenceOf(inboxTask.for, 'Organization') ? 'Organization' : 'Patient';
  return `${resourceType} (No Access)`;
}

export {
  buildNotificationCombinedFilter,
  buildNotificationQueryParamsWithFilters,
  type NotificationQueryParams,
} from '@/services/notification-queries';
